import { useState, useEffect } from 'react';

/**
 * Fetches a candidate's FEC campaign finance summary.
 *
 * Keyed on the FEC candidate ID (S4AK00099), not the bioGuide ID - the two
 * systems share no key, which is what the fec_candidate_id backfill on the
 * member table exists to bridge.
 *
 * Returns the whole record rather than a curated subset so the panel decides
 * what to show. Every money field is passed through untouched: rounding or
 * defaulting here would hide whether a number is genuinely zero or simply
 * absent, and those mean different things on a finance report.
 *
 * @param {string} candidateId
 * @returns {{ financials: Object|null, loading: boolean, error: Error|null }}
 */

const FINANCIALS_ENDPOINT =
  'https://api.govglance.org/elections/election_candidates_financials/';

const useCandidateFinancials = (candidateId) => {
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(Boolean(candidateId));
  const [error, setError] = useState(null);

  const apiKey = process.env.REACT_APP_API_KEY;

  useEffect(() => {
    if (!candidateId) {
      setFinancials(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const fetchFinancials = async () => {
      try {
        const response = await fetch(
          `${FINANCIALS_ENDPOINT}?candidate_id=${encodeURIComponent(candidateId)}&limit=1&skip=0`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${apiKey}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch campaign finances');
        }

        const data = await response.json();

        if (cancelled) return;

        // The endpoint returns an array. A candidate who has filed nothing
        // comes back empty, which is a real answer rather than an error.
        const record = Array.isArray(data) ? data[0] : data;
        setFinancials(record || null);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching campaign finances:', err);
        setFinancials(null);
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFinancials();

    return () => {
      cancelled = true;
    };
  }, [candidateId, apiKey]);

  return { financials, loading, error };
};

export default useCandidateFinancials;